/**
 * Pure link-path helpers behind `useStoryblokLink()`.
 *
 * In multi-site spaces a story link's `full_slug`/`cached_url` comes back as
 * `<site-folder>/<slug>` (e.g. `site-main/about`) and, for non-default
 * languages, with a language prefix on top. Both are internal Storyblok
 * locations — never part of the public URL — so `storyblokLinkPath` strips
 * them and returns a clean app path (`/about`). External links (`https://…`,
 * `mailto:`, `tel:`, `#anchor`) pass through untouched.
 *
 * Kept as free functions (context passed explicitly) so they are unit-testable
 * without a Nuxt runtime — same pattern as `getStoryblokSiteFolders`.
 */

export interface StoryblokLinkField {
	/** Story uuid; empty when the Link field was left blank. */
	id?: string;
	url?: string;
	cached_url?: string;
	linktype?: string;
	email?: string;
	story?: { full_slug?: string };
}

export interface StoryblokLinkContext {
	/** Active site folder (e.g. "site-main"). */
	folder?: string | null;
	/** Default-site folder used as the second fetch attempt. */
	fallbackFolder?: string | null;
	/** Storyblok language code prefixing non-default-locale slugs. */
	language?: string | null;
}

export const stripStoryblokSitePrefixes = (
	slug: string,
	{ folder, fallbackFolder, language }: StoryblokLinkContext = {}
): string => {
	let s = slug.replace(/^\/+/, "");
	if (language && s.startsWith(`${language}/`)) {
		s = s.slice(language.length + 1);
	}
	for (const f of [folder, fallbackFolder]) {
		if (!f) continue;
		if (s === f || s === `${f}/`) return "";
		if (s.startsWith(`${f}/`)) return s.slice(f.length + 1);
	}
	return s;
};

/**
 * True for a Link field the editor left blank. In the default language
 * Storyblok sends it with an empty `cached_url`, but in any other language it
 * sends that language's root instead (e.g. `cached_url: "/en-GB/"`, flagged
 * `prep`). Without this check a blank link reads as a link to the home page,
 * which hides the category fallback on every non-default language.
 *
 * Compared with the active Storyblok language rather than a fixed code shape,
 * so any language code works (e.g. `global`). A real story link always carries
 * the story's id, so it is never treated as blank.
 */
const isBlankStoryLink = (
	link: StoryblokLinkField,
	{ language }: StoryblokLinkContext
): boolean => {
	if (link.linktype !== "story" || link.id || link.story?.full_slug) {
		return false;
	}
	const cached = (link.cached_url ?? "").replace(/^\/+|\/+$/g, "");
	return (
		cached === "" ||
		(!!language && cached.toLowerCase() === language.toLowerCase())
	);
};

export const storyblokLinkPath = (
	link: StoryblokLinkField | null | undefined,
	context: StoryblokLinkContext = {}
): string => {
	if (!link) return "";
	if (link.linktype === "email") {
		return link.email ? `mailto:${link.email}` : (link.cached_url ?? "");
	}
	if (isBlankStoryLink(link, context)) return "";
	const raw = link.story?.full_slug || link.cached_url || link.url || "";
	if (!raw) return "";
	// Absolute URLs, protocol-relative URLs, other schemes and anchors
	// are not site-internal paths — leave them as authored.
	if (isExternalHref(raw)) return raw;
	return `/${stripStoryblokSitePrefixes(raw, context)}`;
};

/**
 * True for hrefs that are not site-internal paths: absolute URLs, protocol-
 * relative URLs, non-http schemes (`mailto:`, `tel:`) and `#anchor`. These must
 * never be given a locale prefix.
 */
export const isExternalHref = (href: string): boolean =>
	/^([a-z][a-z0-9+.-]*:|\/\/|#)/i.test(href);

/**
 * Drops a single leading locale segment from an internal path, e.g.
 * `/se/herr` becomes `/herr`, `/dk` becomes `/` and `/se?x=1` becomes `/?x=1`.
 * Editors sometimes hand-localize a `url` link for one market; stripping the
 * prefix lets the resolver re-apply `localePath()` once for the *active* market
 * instead of leaving the shopper on the authored market (or doubling the
 * prefix). The first segment is read up to the next `/`, `?` or `#` so
 * query/hash links are handled too. Codes are compared case-insensitively.
 */
export const stripLeadingLocalePath = (
	path: string,
	localeCodes: string[]
): string => {
	const s = path.replace(/^\/+/, "");
	const first = /^([^/?#]+)/.exec(s)?.[1];
	if (
		first &&
		localeCodes.some((code) => code.toLowerCase() === first.toLowerCase())
	) {
		const rest = s.slice(first.length);
		if (!rest) return "/";
		return rest.startsWith("/") ? rest : `/${rest}`;
	}
	return `/${s}`;
};

/** Injected Nuxt-runtime helpers for {@link resolveStoryblokHref}. */
export interface ResolveHrefDeps {
	/** `useLocalePath()` - prefixes a path for the active market. */
	localePath: (path: string) => string;
	/** Maps a Norce category Id to its clean path (no leading slash), `""` if none. */
	resolveCategory: (categoryId: string | number) => string;
	/** Active i18n locale codes, lowercased comparison done by the callee. */
	localeCodes: string[];
}

/**
 * The single resolution rule behind `useStoryblokLink().resolveLink()`, kept
 * pure (Nuxt helpers injected via `deps`) so every branch is unit-testable
 * without a runtime. Returns a ready-to-use `:to` href from, in priority order,
 * the link itself then the Norce category `id` fallback:
 *
 *  - external URL, `mailto:` or `#anchor`: returned untouched, never prefixed
 *  - internal story link: clean app path, localized once for the active market
 *  - internal `url` link or a pre-resolved string: any leading market prefix
 *    is dropped first, then localized once (so `/se/herr` opened on `/dk`
 *    becomes `/dk/herr`, not `/se/herr` or `/dk/dk/herr`)
 *  - empty link plus a category `id`: the category path, localized once
 *  - nothing resolvable: `""`, so callers fall back to a localized home
 *
 * Story links are exempt from locale stripping because their slugs are
 * generated and could legitimately equal a locale code (e.g. a page "global").
 */
export const resolveStoryblokHref = (
	link: StoryblokLinkField | string | null | undefined,
	categoryId: string | number | null | undefined,
	context: StoryblokLinkContext,
	deps: ResolveHrefDeps
): string => {
	const { localePath, resolveCategory, localeCodes } = deps;

	const isString = typeof link === "string";
	const path = isString ? (link as string) : storyblokLinkPath(link, context);
	// A hand-entered `url` link (or a pre-resolved string a parent passed) may
	// carry a market prefix; a generated story slug never does.
	const mayStripLocale =
		isString || (link as StoryblokLinkField)?.linktype !== "story";

	if (!path) {
		const hasId =
			categoryId !== undefined && categoryId !== null && categoryId !== "";
		const categoryPath = hasId ? resolveCategory(categoryId!) : "";
		return categoryPath ? localePath(`/${categoryPath}`) : "";
	}

	if (isExternalHref(path)) return path;

	const normalized = mayStripLocale
		? stripLeadingLocalePath(path, localeCodes)
		: path;
	return localePath(normalized);
};
